/**
 * Floating WhatsApp help bubble — bottom-right on all client pages.
 * One tap → opens WhatsApp prefilled with a help-request message to Haithem.
 */
import { useState, useEffect } from "react";
import { MessageCircle, X, HelpCircle } from "lucide-react";
import { WHATSAPP_NUMBER, useLang } from "@/lib/i18n";

const STORAGE_KEY = "mazra3ti:help_tip_seen";

const PREFILL = {
  fr: "Salam Haithem ! J'utilise Mazra3ti et j'aurais besoin d'aide.",
  ar: "السلام عليكم هيثم! نحتاج لمساعدة في استخدام Mazra3ti.",
};

export default function HelpChatBubble() {
  const { lang } = useLang();
  const [showTip, setShowTip] = useState(false);

  // Show a one-time "Need help?" tooltip on first visit
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setShowTip(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const dismissTip = () => {
    setShowTip(false);
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  const openWhatsApp = () => {
    dismissTip();
    const msg = encodeURIComponent(PREFILL[lang] || PREFILL.fr);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="help-chat-bubble"
      className="fixed bottom-24 sm:bottom-5 end-3 sm:end-5 z-40 flex flex-col items-end gap-2 pointer-events-none"
    >
      {showTip && (
        <div
          data-testid="help-chat-tip"
          className="relative pointer-events-auto rounded-2xl bg-white border border-[#E2E4DF] shadow-xl px-4 py-3 max-w-[260px] animate-in slide-in-from-bottom-2 duration-300"
        >
          <button
            type="button"
            onClick={dismissTip}
            data-testid="help-chat-tip-close"
            className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-[#1C3A27] text-white grid place-items-center hover:bg-[#142A1C]"
            aria-label="Fermer"
          >
            <X size={12} />
          </button>
          <div className="flex items-start gap-2">
            <HelpCircle size={18} className="text-[#E05A3D] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-[#1C3A27] leading-tight">
                {lang === "ar" ? "تحتاج لمساعدة؟" : "Besoin d'aide ?"}
              </p>
              <p className="text-xs text-[#5C6B5C] mt-0.5 leading-snug">
                {lang === "ar"
                  ? "اضغط على الفقاعة لمراسلتنا على واتساب."
                  : "Cliquez sur la bulle pour nous écrire sur WhatsApp."}
              </p>
            </div>
          </div>
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-1.5 end-6 w-3 h-3 bg-white border-e border-b border-[#E2E4DF] rotate-45" />
        </div>
      )}

      <button
        type="button"
        onClick={openWhatsApp}
        data-testid="help-chat-bubble-btn"
        title={lang === "ar" ? "اطلب المساعدة عبر واتساب" : "Demander de l'aide via WhatsApp"}
        className="pointer-events-auto group relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1FAB52] text-white shadow-2xl grid place-items-center transition-all hover:scale-110 active:scale-95"
        style={{ boxShadow: "0 10px 25px -5px rgba(37, 211, 102, 0.45)" }}
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <MessageCircle size={26} className="relative z-10" fill="white" stroke="white" strokeWidth={0} />
      </button>
    </div>
  );
}
